import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Inbox, FileText, Briefcase, TrendingUp, MessageCircle, ArrowUpRight } from "lucide-react";
import WeeklyPulse from "./WeeklyPulse";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  recruitment: "Recruitment",
  consulting: "Consulting",
  ai_solutions: "AI Solutions",
  speaking: "Speaking",
  partnership: "Partnership",
  media: "Media",
  general: "General",
};

const STATUS_LABELS = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  proposal_sent: "Proposal sent",
  won: "Won",
  closed: "Closed",
  archived: "Archived",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/stats`, { withCredentials: true })
      .then((r) => setStats(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  }, []);

  if (error) {
    return <div className="text-red-400" data-testid="dashboard-error">{error}</div>;
  }
  if (!stats) {
    return <div className="text-[#A9A9A6]">Loading…</div>;
  }

  const tiles = [
    { label: "Total enquiries", value: stats.total_enquiries, icon: Inbox },
    { label: "Last 7 days", value: stats.last_7_days, icon: TrendingUp },
    { label: "Last 30 days", value: stats.last_30_days, icon: TrendingUp },
    { label: "Conversations", value: stats.conversations || 0, icon: MessageCircle },
    { label: "Case studies", value: stats.case_studies, icon: FileText },
    { label: "Services", value: stats.services, icon: Briefcase },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow">Overview</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl leading-tight tracking-tight">Dashboard</h1>
        </div>
        <Link to="/admin/enquiries" className="btn-ghost">
          Go to enquiries <ArrowUpRight size={14} />
        </Link>
      </div>

      <div className="mt-10">
        <WeeklyPulse />
      </div>

      <section className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-[#343437] border border-[#343437]">
        {tiles.map((t) => (
          <div key={t.label} className="bg-[#0B0B0C] p-6 lg:p-8" data-testid={`stat-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between text-[#A9A9A6]">
              <span className="text-[10px] tracking-[0.22em] uppercase">{t.label}</span>
              <t.icon size={14} />
            </div>
            <p className="oversize mt-6 text-4xl md:text-5xl text-[#D8A936]">{t.value}</p>
          </div>
        ))}
      </section>

      <section className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-[#343437] p-6 lg:p-8">
          <p className="eyebrow">By type</p>
          <ul className="mt-5 space-y-3">
            {Object.entries(TYPE_LABELS).map(([k, label]) => {
              const v = stats.by_type[k] || 0;
              const max = Math.max(1, ...Object.values(stats.by_type));
              const pct = Math.round((v / max) * 100);
              return (
                <li key={k} className="grid grid-cols-[140px_1fr_auto] gap-4 items-center text-sm">
                  <span className="text-[#A9A9A6]">{label}</span>
                  <div className="h-2 bg-[#151517] border border-[#343437]">
                    <div className="h-full bg-[#D8A936]" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="font-display text-[#FAF9F6] w-8 text-right">{v}</span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="border border-[#343437] p-6 lg:p-8">
          <p className="eyebrow">By status</p>
          <ul className="mt-5 space-y-3">
            {Object.entries(STATUS_LABELS).map(([k, label]) => {
              const v = stats.by_status[k] || 0;
              const max = Math.max(1, ...Object.values(stats.by_status));
              const pct = Math.round((v / max) * 100);
              return (
                <li key={k} className="grid grid-cols-[140px_1fr_auto] gap-4 items-center text-sm">
                  <span className="text-[#A9A9A6]">{label}</span>
                  <div className="h-2 bg-[#151517] border border-[#343437]">
                    <div className="h-full bg-[#D8A936]" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="font-display text-[#FAF9F6] w-8 text-right">{v}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    </div>
  );
}
